import type { FocusEvent } from 'react';

/**
 * Everything under a capped sheet's handle, in one scroller. The sheet takes the house cap
 * (`KEYBOARD_SHEET_MAX_HEIGHT` on `DrawerContent`), and this wraps its header, any content and its
 * footer. `ConfirmDeleteDrawer` is always capped. `ConfirmActionDrawer` is capped only while it
 * holds content: without content it is not capped yet (the AIDEV-NOTE on its cap says why), so a
 * long name in its title or description can still run its top off a short screen at a large text
 * size.
 *
 * Inside it, the part that can grow (the delete drawer's header, or the confirm drawer's
 * content between the title and the buttons) takes `overflow-y-auto` and scrolls while the footer,
 * with `shrink-0`, keeps its place. It takes no `overscroll-contain`: at its end, a swipe or a
 * wheel that started on it carries on into this scroller. Contained, it kept them all, so on a
 * phone on its side at a large text size, where it fills nearly all of this scroller, only a swipe
 * on the thin strip under it brought the buttons up. The page behind stays still: this scroller is
 * contained, and where nothing in the drawer can scroll any further, Radix's scroll lock, which
 * every drawer has, stops the swipe (docs/overlays/overlay-system-design.md §1).
 *
 * The part that grows keeps a floor in rem, so at any page zoom or browser font size it shows its
 * first lines rather than shrink to its padding: `min-h-16` on a header, which shows 3rem of the
 * title past its top padding (over two and a half lines), and `min-h-12` on content, which shows
 * two full lines of its text. Where even the floor and the buttons don't fit under the cap (a phone
 * on its side, or upright on a small phone, at a large text size), this scrolls instead, so every
 * button can still be reached.
 *
 * The inset keeps a full-width control's focus ring inside: a scroller clips its sides. The scroll
 * is not on `DrawerContent` itself, where vaul's filler under the sheet (its `::after`, at
 * `top: 100%` with `height: 200%`, `vaul/dist/index.mjs:62`) would scroll with it. Its onFocus is
 * `scrollFocusedIntoView`, below.
 */
export const CAPPED_SHEET_BODY_CLASS =
  '-mx-1 flex min-h-0 flex-col overflow-y-auto overscroll-contain px-1';

/**
 * The capped sheet body's onFocus: brings a control focused from the keyboard into view in it.
 * Radix's focus trap moves focus with `preventScroll` as Tab wraps from the last control to the
 * first (`@radix-ui/react-focus-scope`), which in a scrolled body could leave that control mostly
 * out of view.
 *
 * Keyboard focus only (`:focus-visible`), the rule `src/utils/keepFocusedRowInView.ts` keeps for the
 * Space pickers' lists: a tap focuses its button too (Chrome), and a scroll then moves what is under
 * the finger, or, where a drawer focuses a button as it opens (the partnership delete's Cancel),
 * takes the title out of view on a phone on its side at a large text size. A text field matches
 * `:focus-visible` however it takes focus, so a note the trap wraps to is brought into view too.
 */
export function scrollFocusedIntoView(event: FocusEvent): void {
  const { target } = event;
  if (target === event.currentTarget || !target.matches(':focus-visible')) return;
  target.scrollIntoView({ block: 'nearest' });
}
